import React from "react";
import { Link } from "react-router-dom";
import "./ServicesPage.css";
import useRevealOnIntersect from "../hooks/useRevealOnIntersect";
import { useScrollToTopOnMount } from "../hooks/useScrollToTop";

const services = [
  {
    key: "security",
    title: "Security",
    description:
      "Advanced surveillance, AI-powered threat detection, and smart safety systems for a secure digital and physical environment.",
    basePath: "/security",
    highlights: ["AI analytics", "Access control", "Cloud/Hybrid VMS"],
    icon: "🛡️",
  },
  {
    key: "network",
    title: "Network",
    description:
      "Seamless IoT integration, fiber optic deployments, and digital transformation to keep your enterprise connected.",
    basePath: "/network",
    highlights: ["Wi‑Fi 6/6E", "SD‑WAN", "Zero‑trust"],
    icon: "📡",
  },
  {
    key: "it-infrastructure",
    title: "IT Infrastructure",
    description:
      "Robust IT setups including access control, power-efficient devices, and solar-powered infrastructure solutions.",
    basePath: "/it-infrastructure",
    highlights: ["Servers & storage", "Backups & DR", "Identity & access"],
    icon: "🧩",
  },
  {
    key: "general-order-supply",
    title: "General Order Supply",
    description:
      "Reliable sourcing and delivery of quality equipment, tools, and materials for every industry.",
    basePath: "/general-order-supply",
    highlights: ["Vetted vendors", "QA & docs", "On‑time delivery"],
    icon: "📦",
  },
];

export default function ServicesPage() {
  useScrollToTopOnMount();
  useRevealOnIntersect(".sp-reveal", { threshold: 0.15 });
  return (
    <main className="services-page">
      <header className="sp-hero">
        <div className="sp-container">
          <h1>Enterprise Service Portfolio</h1>
          <p className="subtitle" aria-label="Services overview statement">
            A unified suite covering security, networking, infrastructure and supply—architected for reliability, scalability and measurable operational gains.
          </p>
          <p className="sp-hero-subtext sp-reveal" style={{ "--d": "140ms" }}>
            Every engagement follows a proven lifecycle (assessment → design → implementation → validation → optimization) to reduce incidents, accelerate performance and harden compliance posture.
          </p>
          <div className="sp-hero-pills" role="list" aria-label="Key service value points">
            {[
              { label: "Zero‑Trust Ready" },
              { label: "Telemetry Driven" },
              { label: "Scalable Design" }
            ].map((pill,i) => (
              <span
                key={pill.label}
                role="listitem"
                className="sp-hero-pill sp-reveal"
                style={{ "--d": `${180 + i*80}ms` }}
              >{pill.label}</span>
            ))}
          </div>
        </div>
        <div className="sp-accent" aria-hidden="true" />
        {/* Decorative animated background elements */}
        <div className="sp-hero-bg" aria-hidden="true">
          <span className="sp-blob b1" />
          <span className="sp-blob b2" />
          <span className="sp-blob b3" />
          <span className="sp-grid-glow" />
        </div>
      </header>

      <section className="sp-section">
        <div className="sp-container">
          <div className="sp-grid">
            {services.map(({ key, title, description, basePath, highlights, icon }) => (
              <Link
                key={key}
                to={basePath}
                className="sp-card sp-reveal"
                style={{ "--d": `${services.findIndex(s => s.key === key) * 90}ms` }}
                aria-label={`View ${title} service details`}
              >
                <div className="sp-card-inner">
                  <div className="sp-card-head">
                    <div className="sp-icon" aria-hidden="true">{icon}</div>
                    <h2>{title}</h2>
                    <p className="sp-desc">{description}</p>
                  </div>
                  <ul className="sp-bullets" aria-label={`${title} highlights`}>
                    {highlights.map(h => (
                      <li key={h}>{h}</li>
                    ))}
                  </ul>
                  <div className="sp-card-meta" aria-hidden="true">
                    <span className="sp-arrow" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics / KPIs band */}
      <section className="sp-metrics">
        <div className="sp-container">
          <div className="sp-metrics-grid">
            {[
              { label: "Avg Uptime", value: "99.982%", note: "Across managed networks" },
              { label: "Deployments", value: "250+", note: "Completed last 12 mo" },
              { label: "Incident Reduction", value: "47%", note: "Post optimization" },
              { label: "Client Satisfaction", value: "96%", note: "Survey feedback" },
            ].map((m, i) => (
              <div key={m.label} className="sp-metric sp-reveal" style={{ "--d": `${i * 80}ms` }}>
                <div className="sp-metric-value">{m.value}</div>
                <div className="sp-metric-label">{m.label}</div>
                <div className="sp-metric-note">{m.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery process timeline */}
      <section className="sp-process">
        <div className="sp-container">
          <h2 className="sp-reveal" style={{ "--d": "40ms" }}>How We Deliver</h2>
          <p className="sp-lead sp-reveal" style={{ "--d": "120ms" }}>A proven, quality-assured lifecycle from assessment to ongoing optimization.</p>
          <ol className="sp-process-steps">
            {[
              { title: "Assessment", text: "Validate goals, current state, and constraints." },
              { title: "Design", text: "Produce architecture, bill of materials, and timeline." },
              { title: "Implementation", text: "Deploy hardware/software with verified checkpoints." },
              { title: "Validation", text: "Run tests, harden policies, performance tuning." },
              { title: "Optimization", text: "Continuous improvements via telemetry & feedback." },
            ].map((step, i) => (
              <li key={step.title} className="sp-step sp-reveal" style={{ "--d": `${160 + i * 100}ms` }}>
                <div className="sp-step-index">{i + 1}</div>
                <div className="sp-step-body">
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Industry use cases */}
      <section className="sp-usecases">
        <div className="sp-container">
          <h2 className="sp-reveal" style={{ "--d": "40ms" }}>Industry Focus</h2>
          <p className="sp-lead sp-reveal" style={{ "--d": "120ms" }}>Solutions tailored to operational realities and compliance requirements.</p>
          <div className="sp-use-grid">
            {[
              { icon: "🏢", title: "Enterprise Campuses", text: "Secure, segmented high-performance connectivity." },
              { icon: "🏪", title: "Retail", text: "AI video analytics & resilient PoS networks." },
              { icon: "🏭", title: "Industrial / OT", text: "Deterministic edge + segmentation for PLCs." },
              { icon: "🏥", title: "Healthcare", text: "Identity, compliance & monitored IoT surfaces." },
              { icon: "🏫", title: "Education", text: "Scalable Wi‑Fi and safe surveillance systems." },
              { icon: "🚚", title: "Logistics", text: "Tracking, secure access and optimized endpoints." },
            ].map((c, i) => (
              <div key={c.title} className="sp-use sp-reveal" style={{ "--d": `${160 + i * 70}ms` }}>
                <div className="sp-use-icon" aria-hidden="true">{c.icon}</div>
                <h3>{c.title}</h3>
                <p>{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="sp-faq">
        <div className="sp-container">
          <h2 className="sp-reveal" style={{ "--d": "40ms" }}>FAQ</h2>
          <div className="sp-faq-grid">
            {[
              { q: "Can you work with existing vendors?", a: "Yes—our designs integrate with most mainstream platforms while improving observability and resilience." },
              { q: "Do you provide managed services?", a: "We offer tiered support: monitoring, incident response, change management, and quarterly optimization reviews." },
              { q: "How is pricing structured?", a: "Fixed-scope phases + optional retainers for ongoing improvements and telemetry-backed tuning." },
              { q: "Security compliance support?", a: "We align implementations with ISO and local regulatory controls, providing evidence artifacts where needed." },
            ].map((f, i) => (
              <details key={f.q} className="sp-faq-item sp-reveal" style={{ "--d": `${120 + i * 90}ms` }}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="sp-partners">
        <div className="sp-container">
          <h2 className="sp-reveal" style={{ "--d": "40ms" }}>Technology Ecosystem</h2>
          <p className="sp-lead sp-reveal" style={{ "--d": "120ms" }}>Strategic alignment with leading platforms and frameworks.</p>
          <div className="sp-partner-row">
            {['EdgeCloud','SecureNet','VisionAI','DataMesh','AuthCore','IoTGrid'].map((p,i)=>(
              <div key={p} className="sp-partner sp-reveal" style={{ "--d": `${160 + i*70}ms` }}>
                <span className="sp-partner-badge">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep CTA band */}
      <section className="sp-band">
        <div className="sp-band-inner sp-container">
          <div className="sp-band-text sp-reveal" style={{ "--d": "60ms" }}>
            <h2>Ready to accelerate your environment?</h2>
            <p>Let’s co-design a roadmap that reduces incidents, boosts performance, and establishes measurable reliability targets.</p>
          </div>
          <div className="sp-band-actions sp-reveal" style={{ "--d": "140ms" }}>
            <Link to="/#contact" className="sp-cta-btn">Start the Conversation</Link>
          </div>
        </div>
        <div className="sp-band-bg" aria-hidden="true">
          <span className="sp-band-blob b1" />
          <span className="sp-band-blob b2" />
        </div>
      </section>

      <section className="sp-cta">
        <div className="sp-container">
          <h3>Need help choosing?</h3>
          <p className="subtitle">Tell us about your goals—we’ll recommend the right mix of capabilities.</p>
          <Link to="/#contact" className="sp-cta-btn">Talk to us</Link>
        </div>
      </section>
    </main>
  );
}